import { FC, ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { Colors } from "../../../configs/colors";
import { ADMIN_NAV, IAdminNavGroup, findAdminNavItem } from "./admin-nav-items";
import { primaryAlpha } from "../../../configs/colors";
import { useStoreBranding } from "../../../util/store-branding";

export const ADMIN_SIDE_NAV_WIDTH = "22rem";

const BORDER = "#E5E7EB";
const TEXT = "#4B5563";
const GREEN_SOFT = "#EAF8F1";
const GREEN_LIGHTER = "#F4FBF7";

export const NavSvgIcon: FC<{ children: ReactNode; size?: string; color?: string }> = ({
  children,
  size = "1.4rem",
  color,
}) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    sx={{ width: size, height: size, color, flexShrink: 0 }}
  >
    {children}
  </Box>
);

const GroupButton: FC<{
  group: IAdminNavGroup;
  active: boolean;
  open: boolean;
  onClick(): void;
}> = ({ group, active, open, onClick }) => {
  const hasChildren = group.children.length > 0;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-expanded={hasChildren ? open : undefined}
      aria-current={!hasChildren && active ? "page" : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "0.85rem",
        width: "100%",
        p: "0.75rem 0.85rem",
        border: "none",
        borderRadius: "1rem",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
        fontSize: "var(--fs-base)",
        fontWeight: 600,
        color: active ? Colors.PRIMARY : TEXT,
        backgroundColor: active ? GREEN_SOFT : "transparent",
        transition: "background-color 0.15s",
        "&:hover": { backgroundColor: active ? GREEN_SOFT : "#F3F4F6" },
        "&:focus-visible": { outline: `2px solid ${Colors.PRIMARY}`, outlineOffset: "1px" },
      }}
    >
      <Box
        component="span"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "2.75rem",
          height: "2.75rem",
          borderRadius: "0.85rem",
          backgroundColor: active ? primaryAlpha(0.10) : "#F3F4F6",
          flexShrink: 0,
        }}
      >
        <NavSvgIcon>{group.icon}</NavSvgIcon>
      </Box>
      <Box
        component="span"
        sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {group.title}
      </Box>
      {hasChildren && (
        <Box
          component="span"
          sx={{
            display: "flex",
            transform: open ? "rotate(90deg)" : "none",
            transition: "transform 0.2s",
          }}
        >
          <NavSvgIcon size="1.05rem">
            <polyline points="9 18 15 12 9 6" />
          </NavSvgIcon>
        </Box>
      )}
    </Box>
  );
};

export interface IAdminSideNavProps {
  /** Called after a page is picked (closes the menu on small screens) */
  onNavigate?(): void;
}

export const AdminSideNav: FC<IAdminSideNavProps> = ({ onNavigate }) => {
  const branding = useStoreBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const { group: activeGroup, item: activeItem } = findAdminNavItem(location.pathname);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    activeGroup ? { [activeGroup.title]: true } : {}
  );

  // The group of the page being shown is always open
  useEffect(() => {
    if (activeGroup && activeGroup.children.length) {
      setOpenGroups((prev) =>
        prev[activeGroup.title] ? prev : { ...prev, [activeGroup.title]: true }
      );
    }
  }, [activeGroup]);

  const go = (link: string) => {
    navigate(link);
    onNavigate?.();
  };

  const handleGroupClick = (group: IAdminNavGroup) => {
    if (!group.children.length) {
      if (group.link) go(group.link);
      return;
    }
    const isOpen = !!openGroups[group.title];
    setOpenGroups((prev) => ({ ...prev, [group.title]: !isOpen }));
    // Opening a group shows its first page, unless one of its pages is open
    if (!isOpen && activeGroup !== group) go(group.children[0].link);
  };

  return (
    <Box
      component="aside"
      aria-label="Admin navigation"
      sx={{
        width: ADMIN_SIDE_NAV_WIDTH,
        flexShrink: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#ffffff",
        borderRight: `1px solid ${BORDER}`,
        boxShadow: "1px 0 4px rgba(0,0,0,0.04)",
      }}
    >
      {/* Brand */}
      <Box
        sx={{
          background: "linear-gradient(135deg,#0F172A 0%,#1E293B 100%)",
          p: "1.85rem 1.7rem",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: "5.2rem",
            height: "5.2rem",
            borderRadius: "50%",
            backgroundColor: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: "4.35rem",
              height: "4.35rem",
              borderRadius: "50%",
              backgroundColor: Colors.PRIMARY,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "var(--fs-xl)",
              fontWeight: 700,
              color: "#ffffff",
              overflow: "hidden",
            }}
          >
            {branding?.logo ? (
              <Box
                component="img"
                src={branding.logo}
                alt="Store logo"
                sx={{ width: "100%", height: "100%", objectFit: "contain", backgroundColor: "#ffffff", p: "0.35rem" }}
              />
            ) : (
              "P"
            )}
          </Box>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "var(--fs-2xs)",
              fontWeight: 700,
              letterSpacing: "0.16em",
              color: "rgba(255,255,255,0.55)",
              textTransform: "uppercase",
              lineHeight: 1.3,
            }}
          >
            Presto POS
          </Typography>
          <Typography sx={{ fontSize: "var(--fs-md)", fontWeight: 800, color: "#ffffff", lineHeight: 1.3 }}>
            Admin Panel
          </Typography>
          <Typography
            sx={{ fontSize: "var(--fs-2xs)", fontWeight: 500, color: "rgba(255,255,255,0.5)", lineHeight: 1.3 }}
          >
            System Configuration
          </Typography>
        </Box>
      </Box>

      {/* Menu */}
      <Box component="nav" sx={{ flex: 1, overflowY: "auto", p: "1.35rem 1rem" }}>
        <Typography
          sx={{
            fontSize: "var(--fs-2xs)",
            fontWeight: 700,
            letterSpacing: "0.18em",
            color: "#9CA3AF",
            textTransform: "uppercase",
            m: "0 0 0.85rem 0.7rem",
          }}
        >
          Main Menu
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
          {ADMIN_NAV.map((group) => {
            const active = activeGroup === group;
            const open = !!openGroups[group.title];
            return (
              <Box key={group.title}>
                <GroupButton
                  group={group}
                  active={active}
                  open={open}
                  onClick={() => handleGroupClick(group)}
                />
                {group.children.length > 0 && open && (
                  <Box
                    sx={{
                      ml: "2.35rem",
                      mt: "0.35rem",
                      mb: "0.35rem",
                      pl: "1rem",
                      borderLeft: `1.5px solid ${BORDER}`,
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.15rem",
                    }}
                  >
                    {group.children.map((child) => {
                      const activeChild = activeItem === child;
                      return (
                        <Box
                          key={child.title}
                          component="button"
                          type="button"
                          onClick={() => go(child.link)}
                          aria-current={activeChild ? "page" : undefined}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.5rem",
                            width: "100%",
                            p: "0.65rem 0.85rem",
                            borderRadius: "0.85rem",
                            cursor: "pointer",
                            textAlign: "left",
                            fontFamily: "inherit",
                            fontSize: "var(--fs-xs)",
                            fontWeight: 500,
                            border: `1px solid ${activeChild ? "#D6F1E4" : "transparent"}`,
                            backgroundColor: activeChild ? GREEN_LIGHTER : "transparent",
                            color: activeChild ? Colors.PRIMARY : TEXT,
                            "&:hover": { backgroundColor: activeChild ? GREEN_LIGHTER : "#F9FAFB" },
                            "&:focus-visible": { outline: `2px solid ${Colors.PRIMARY}`, outlineOffset: "1px" },
                          }}
                        >
                          <Box
                            component="span"
                            sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            {child.title}
                          </Box>
                          {activeChild && (
                            <Box
                              component="span"
                              sx={{
                                width: "0.5rem",
                                height: "0.5rem",
                                borderRadius: "50%",
                                backgroundColor: Colors.PRIMARY,
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          borderTop: "1px solid #F0F0F0",
          p: "1.2rem 1.35rem",
          backgroundColor: "#FAFAFA",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            borderRadius: "1rem",
            border: `1px solid ${BORDER}`,
            backgroundColor: "#ffffff",
            p: "0.85rem 1rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <Typography sx={{ fontSize: "var(--fs-xs)", fontWeight: 700, color: TEXT }}>
            Presto Admin Panel
          </Typography>
          <Typography sx={{ fontSize: "var(--fs-2xs)", color: "#6B7280", mt: "0.15rem" }}>
            Presto Valet &copy; {new Date().getFullYear()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
